/**
 * One-time "Allow notifications" sheet.
 *
 * Shown to every signed-in user whose device has not yet decided on
 * notifications - existing users see it on their next login, new users
 * on their first session. Dismissing hides it for 14 days; allowing or a
 * hard "denied" hides it for good on that device.
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BellRing, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { isNativeApp } from '../../utils/nativeBridge';
import { nativePushSupported, nativePushPermission, registerNativePushToken } from '../../utils/nativePush';

const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;
const HIDDEN_ON = ['/auth', '/onboarding', '/signup', '/login', '/verify-email', '/reset-password', '/portal', '/welcome'];
const keyFor = (uid) => `akf_push_prompt_${uid}`;

export default function PushOptInPrompt() {
  const { user } = useAuth() || {};
  const location = useLocation();
  const { requestPermission } = usePushNotifications(!!user);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const uid = user?.id;

  useEffect(() => {
    if (!uid) { setOpen(false); return undefined; }
    if (HIDDEN_ON.some((p) => location.pathname.startsWith(p))) { setOpen(false); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const raw = localStorage.getItem(keyFor(uid));
        if (raw) {
          const st = JSON.parse(raw);
          if (st.done || (st.snoozedAt && Date.now() - st.snoozedAt < SNOOZE_MS)) return;
        }
        let undecided = false;
        if (isNativeApp()) {
          // Wrapper must expose the push bridge; older builds simply never show the sheet.
          if (!nativePushSupported()) return;
          const perm = await nativePushPermission();
          if (perm === 'granted') { registerNativePushToken(); localStorage.setItem(keyFor(uid), JSON.stringify({ done: true })); return; }
          undecided = perm !== 'denied';
        } else {
          if (!('Notification' in window) || !('PushManager' in window) || !('serviceWorker' in navigator)) return;
          if (Notification.permission === 'denied') { localStorage.setItem(keyFor(uid), JSON.stringify({ done: true })); return; }
          undecided = Notification.permission === 'default';
        }
        // Small delay so the sheet never fights the first-paint of the dashboard.
        if (undecided && !cancelled) setTimeout(() => { if (!cancelled) setOpen(true); }, 2500);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [uid, location.pathname]);

  const finish = (state) => {
    try { localStorage.setItem(keyFor(uid), JSON.stringify(state)); } catch { /* noop */ }
    setOpen(false);
  };

  const allow = async () => {
    setBusy(true);
    try {
      let granted = false;
      if (isNativeApp()) {
        const r = await registerNativePushToken({ requestPermission: true });
        granted = r.ok;
        if (!granted && r.reason === 'denied') { finish({ done: true }); toast.info('You can turn notifications on later in your phone settings.'); return; }
      } else {
        granted = await requestPermission();
        if (!granted && Notification.permission === 'denied') { finish({ done: true }); return; }
      }
      if (granted) { finish({ done: true }); toast.success("You're set - we'll alert you when something needs attention."); }
      else finish({ snoozedAt: Date.now() });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          className="fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+76px)] sm:bottom-6 sm:right-6 sm:left-auto sm:w-[380px] z-[60]"
          data-testid="push-optin-prompt"
        >
          <div className="rounded-2xl bg-[#0F1F4B] text-white shadow-[0_18px_50px_rgba(15,31,75,0.45)] p-4 relative overflow-hidden">
            <button type="button" onClick={() => finish({ snoozedAt: Date.now() })} aria-label="Not now"
              className="absolute top-2.5 right-2.5 p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10" data-testid="push-optin-dismiss">
              <X className="w-4 h-4" />
            </button>
            <div className="flex gap-3 items-start">
              <div className="w-10 h-10 rounded-xl bg-[#108981]/25 flex items-center justify-center shrink-0">
                <BellRing className="w-5 h-5 text-[#7FE0D2]" />
              </div>
              <div className="min-w-0 pr-6">
                <p className="text-[15px] font-bold leading-tight">Get alerts on this device</p>
                <p className="text-[12.5px] text-white/70 mt-1 leading-snug">
                  Bills due, money in, reconciliation gaps and your morning summary - as they happen, with a badge on the app icon.
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-3.5">
              <button type="button" onClick={allow} disabled={busy} data-testid="push-optin-allow"
                className="flex-1 h-10 rounded-full bg-gradient-to-r from-[#283F8B] to-[#108981] text-sm font-bold disabled:opacity-60 active:brightness-95">
                {busy ? 'Turning on…' : 'Allow notifications'}
              </button>
              <button type="button" onClick={() => finish({ snoozedAt: Date.now() })} data-testid="push-optin-later"
                className="h-10 px-4 rounded-full text-sm font-semibold text-white/80 hover:bg-white/10">
                Not now
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
